import { useQuery, useSuspenseQuery } from "@tanstack/react-query";
import { createFileRoute, Link } from "@tanstack/react-router";
import { useEffect, useState } from "react";

import {
  useMergeEvents,
  usePublishEvent,
  usePublishSeries,
  useRejectEvent,
} from "../features/admin/api/mutations";
import { adminQueries, viewerQueries } from "../features/admin/api/queries";
import { QueueRail } from "../features/admin/components/QueueRail";
import { ReviewDeckCard } from "../features/admin/components/ReviewDeckCard";
import { SeriesReviewCard } from "../features/admin/components/SeriesReviewCard";
import { gqlErrorMessage } from "../shared/lib/gql-error";
import { AppHeader } from "../shared/ui/AppHeader";

/**
 * How many rail rows to fetch at once.
 *
 * Higher than the old page size of twenty, because a rail row is a fraction of
 * what a card cost: title, time, venue and a duplicate count, against a full
 * event with organisers, links, images, a forecast and its series. The rail is
 * the whole worklist in one fetch rather than a page of it, so there is no pager
 * to lose your place in.
 *
 * Still capped. Eighty is comfortably more than a session's work, and an
 * uncapped list would grow without bound as syncs stage more.
 */
const RAIL_SIZE = 80;

/** The repeating half is still paged, and still a separate decision. */
const SERIES_PAGE_SIZE = 20;

export const Route = createFileRoute("/_authenticated/admin/review")({
  /*
   * The viewer is loaded here so the component can tell "not an admin" apart
   * from "no pending events" — both are empty screens otherwise, and the first
   * one needs an explanation while the second is good news.
   *
   * The queue itself is NOT loaded in the loader: it throws FORBIDDEN for a
   * non-admin, and a loader throw would blank the route rather than render the
   * explanation below.
   */
  loader: ({ context: { queryClient } }) =>
    queryClient.ensureQueryData(viewerQueries.current()),
  component: ReviewPage,
});

function ReviewPage() {
  const { data: viewerData } = useSuspenseQuery(viewerQueries.current());

  return (
    <>
      <AppHeader />
      <main className="mx-auto max-w-6xl p-6">
        <h1 className="text-2xl font-semibold">Review queue</h1>

        {viewerData.viewer.isAdmin ? (
          <Deck />
        ) : (
          /*
           * The affordance is hidden from the nav for a non-admin, but the URL is
           * still typeable — so the route says so plainly rather than rendering an
           * empty list that reads like "nothing to review".
           *
           * This is not the security boundary. The server refuses the query and
           * every mutation regardless of what this component renders.
           */
          <p className="mt-4 rounded-md bg-surface-muted px-3 py-2 text-sm text-content-muted">
            This page is for administrators. Nothing here is hidden from you by
            this screen alone — the server refuses these actions too.
          </p>
        )}
      </main>
    </>
  );
}

/**
 * The deck: one event under review, the rest listed beside it.
 *
 * ## Why one at a time
 *
 * The queue used to render every pending event as a full card. That made the
 * duplicate check unaffordable — a same-day lookup per card, twenty cards a page
 * — so it went behind a disclosure, and a check nobody clicks does not prevent
 * the mistake it exists for. Reviewing one at a time reverses the arithmetic:
 * one card means one lookup, so its duplicates come down with it.
 *
 * A separate component from the route so its queries only run for an admin —
 * mounting it unconditionally would fire requests that are *meant* to fail.
 */
function Deck() {
  /*
   * Which event is on screen, or null to mean "the first one still waiting".
   *
   * Deliberately an id and not an index: publishing removes a row, and an index
   * would silently point at whatever slid into its place. Null rather than
   * defaulting to `rows[0].id` because the rail has not resolved yet on the
   * first render, and there is no id to hold.
   */
  const [selectedId, setSelectedId] = useState<string | null>(null);
  /*
   * Where the selected row sat before it vanished.
   *
   * Publishing removes the row from the rail, so the id no longer resolves — and
   * falling back to `rows[0]` sent the reviewer to the top of the queue after
   * every decision, losing their place. Remembering the index means the next
   * card is the one that slid into the gap, which is what "next" means when the
   * list shrinks under you.
   */
  const [lastIndex, setLastIndex] = useState(0);

  const { data: railData, error: railError } = useSuspenseQuery({
    ...adminQueries.queue({ limit: RAIL_SIZE, offset: 0 }),
    /*
     * A FORBIDDEN here is a real answer, not a transient failure — retrying it
     * three times just makes the same refusal three times.
     */
    retry: false,
  });

  const { data: seriesData, error: seriesError } = useSuspenseQuery({
    ...adminQueries.pendingSeries({ limit: SERIES_PAGE_SIZE, offset: 0 }),
    retry: false,
  });

  const rows = railData.eventPage.items;
  const totalCount = railData.eventPage.totalCount;

  /*
   * The card to show.
   *
   * When the selected id is still in the rail, that is the answer. When it is
   * not — the normal case straight after publishing or rejecting, since the
   * mutation invalidates the rail and the decided row leaves it — fall back to
   * whatever now occupies the same position. That IS the auto-advance: the row
   * below slides up into the gap, so the reviewer keeps moving down the queue
   * instead of being thrown back to the top.
   *
   * Clamped, because the vanished row may have been the last one.
   */
  const selected = rows.find((row) => row.id === selectedId);
  const current =
    selected ?? rows[Math.min(lastIndex, rows.length - 1)] ?? null;
  const index = current ? rows.findIndex((row) => row.id === current.id) : -1;
  const position = index + 1;

  /*
   * Not `useSuspenseQuery`: this must not suspend the rail beside it, which
   * would blank the whole worklist to load one card. `enabled` handles the
   * empty queue, where there is no id to ask about.
   */
  const {
    data: eventData,
    error: eventError,
    isLoading: eventLoading,
  } = useQuery(adminQueries.reviewEvent(current?.id));

  const publish = usePublishEvent();
  const publishSeries = usePublishSeries();
  const reject = useRejectEvent();
  const merge = useMergeEvents();

  const busy =
    publish.isPending ||
    publishSeries.isPending ||
    reject.isPending ||
    merge.isPending;
  const failure =
    publish.error ?? publishSeries.error ?? reject.error ?? merge.error;

  /*
   * Move to the next row without deciding.
   *
   * Skip is what makes one-at-a-time review honest: without it, an event the
   * reviewer is unsure about blocks the queue, and the pressure is to publish or
   * reject just to move on. Wraps to the start rather than dead-ending, so a
   * skipped event comes back around.
   */
  const step = (delta: number) => {
    if (rows.length === 0) return;
    const next = (index + delta + rows.length) % rows.length;
    setSelectedId(rows[next]?.id ?? null);
    setLastIndex(next);
  };

  /** Pick a row outright, from the rail. */
  const select = (id: string) => {
    setSelectedId(id);
    setLastIndex(rows.findIndex((row) => row.id === id));
  };

  /*
   * Keyboard review. One-at-a-time only pays off if a reviewer can work without
   * reaching for the mouse each time, and the arrows are the whole navigation
   * model once there is exactly one card.
   *
   * Ignored while focus is in a text field, or the rejection reason box would
   * eat every keystroke as a command.
   */
  useEffect(() => {
    const onKey = (pressed: KeyboardEvent) => {
      const target = pressed.target as HTMLElement | null;
      if (
        target?.tagName === "INPUT" ||
        target?.tagName === "TEXTAREA" ||
        target?.isContentEditable === true
      ) {
        return;
      }
      if (pressed.metaKey || pressed.ctrlKey || pressed.altKey) return;

      if (pressed.key === "ArrowRight") step(1);
      else if (pressed.key === "ArrowLeft") step(-1);
      else if (pressed.key === "p" && current && !busy) {
        publish.mutate(current.id);
      }
    };

    window.addEventListener("keydown", onKey);
    return () => {
      window.removeEventListener("keydown", onKey);
    };
    /*
     * No dependency array, deliberately: the handler closes over `current`,
     * `rows` and `busy`, and a stale one would publish the wrong event or refuse
     * to move. Rebinding one listener per render is cheaper than the bug, and
     * the cleanup makes it exact rather than accumulating.
     */
  });

  if (railError ?? seriesError) {
    return (
      <p role="alert" className="mt-4 text-sm text-brand">
        {gqlErrorMessage(
          railError ?? seriesError,
          "Could not load the review queue.",
        )}
      </p>
    );
  }

  const pendingSeries = seriesData.pendingEventSeriesPage.items;
  const seriesTotal = seriesData.pendingEventSeriesPage.totalCount;

  if (rows.length === 0 && pendingSeries.length === 0) {
    return (
      <div className="mt-6 rounded-lg border border-dashed border-border-subtle p-6 text-center">
        <p className="text-sm text-content-muted">
          Nothing waiting for review.
        </p>
        <Link
          to="/events"
          className="mt-2 inline-block text-sm text-brand underline"
        >
          Back to events
        </Link>
      </div>
    );
  }

  return (
    <>
      <p className="mt-1 text-sm text-content-muted">
        Nothing here is publicly visible yet. Publishing is the only thing that
        puts an event on the listing.
      </p>

      {failure && (
        <p role="alert" className="mt-4 text-sm text-brand">
          {gqlErrorMessage(failure, "That review action did not go through.")}
        </p>
      )}

      <div className="mt-5 grid items-start gap-5 lg:grid-cols-[minmax(0,1fr)_19rem]">
        <div className="flex flex-col gap-5">
          {eventError ? (
            <p role="alert" className="text-sm text-brand">
              {gqlErrorMessage(eventError, "Could not load that event.")}
            </p>
          ) : eventData?.event ? (
            <ReviewDeckCard
              /*
               * Keyed on the id so the card's own state — a half-typed rejection
               * reason — does not follow the reviewer to the next event. Moving
               * on is a fresh decision and should look like one.
               */
              key={eventData.event.id}
              event={eventData.event}
              position={position}
              totalCount={totalCount}
              busy={busy}
              onPublish={(id) => {
                publish.mutate(id);
              }}
              onReject={(id, reason) => {
                reject.mutate({ id, reason });
              }}
              onMerge={(keepId, mergeIds, publish, rule) => {
                merge.mutate({ keepId, mergeIds, publish, rule });
              }}
              onSkip={() => {
                step(1);
              }}
            />
          ) : eventLoading ? (
            <p className="text-sm text-content-muted">Loading the event…</p>
          ) : rows.length === 0 ? (
            <div className="rounded-lg border border-dashed border-border-subtle p-6 text-center">
              <p className="text-sm text-content-muted">
                No single events waiting. The repeating ones are on the right.
              </p>
            </div>
          ) : null}

          {/*
            Repeating events keep their own card type and stay below the deck
            rather than in it: approving a rule publishes every night it
            generates, which is not the same weight of decision as publishing one
            evening. Rendering them through the deck would make them look like it.
          */}
          {pendingSeries.length > 0 && (
            <section>
              <h2 className="text-sm font-medium">
                Repeating events ({seriesTotal})
              </h2>
              <p className="mt-1 text-xs text-content-muted">
                Approving one of these generates every night it describes.
              </p>
              <ul className="mt-3 flex flex-col gap-4">
                {pendingSeries.map((series) => (
                  <SeriesReviewCard
                    key={series.id}
                    series={series}
                    busy={busy}
                    onPublish={(id) => {
                      publishSeries.mutate(id);
                    }}
                  />
                ))}
              </ul>
            </section>
          )}
        </div>

        <div className="flex flex-col gap-4 lg:sticky lg:top-5">
          <QueueRail
            rows={rows}
            currentId={current?.id ?? null}
            totalCount={totalCount}
            busy={busy}
            onSelect={select}
          />

          <p className="px-1 text-xs text-content-muted">
            <kbd className="rounded border border-border-subtle px-1">←</kbd>{" "}
            <kbd className="rounded border border-border-subtle px-1">→</kbd>{" "}
            move ·{" "}
            <kbd className="rounded border border-border-subtle px-1">P</kbd>{" "}
            publish
          </p>
        </div>
      </div>
    </>
  );
}
